<script setup lang="ts">
import {
  type Component,
  computed,
  type CSSProperties,
  inject,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
  type VNode,
  watch,
} from "vue";
import { MenuEntry } from "../../models/menu-entry";
import type { Placement } from "../../models/types";
import BcMenuEntry from "./bc-menu-entry.vue";
import {
  CONTROLS_DESIGN_VERSION,
  DEFAULT_CONTROLS_DESIGN_VERSION,
  DEFAULT_DESIGN_VERSION,
  DESIGN_VERSION,
} from "../../models";

export type BcMenuProps = {
  x?: number;
  y?: number;
  items?: MenuEntry[];
  isVisible?: boolean;
  separateAll?: boolean;
  /* Only works whene there are no sub-items in items */
  numberOfVisibleItems?: number;
  disableHeightCalculation?: boolean;
  /* Used for bc-popup-menu position calulation */
  parentRect?: HTMLElement | null;
  /** Specifies the placement of the menu items compared to the container element */
  placement?: Placement;
  fullWidth?: boolean;
  textWrap?: boolean;
  subItemIcon?: VNode | Component;
  selectedItem?: MenuEntry;
  offset?: number;
  teleportTarget?: string;
};

export type BcMenuEmits = {
  (event: "click", entry: MenuEntry): void;
};

const emit = defineEmits<BcMenuEmits>();
const design = inject(
  DESIGN_VERSION,
  computed(() => DEFAULT_DESIGN_VERSION),
);
const controlsDesign = inject(
  CONTROLS_DESIGN_VERSION,
  computed(() => DEFAULT_CONTROLS_DESIGN_VERSION),
);
const pak = computed(() => `bc-controls-d${controlsDesign.value}`);

const menuRef = useTemplateRef("menuRef");
const subMenuRef = useTemplateRef("subMenuRef");
const teleportTargetRef = useTemplateRef("teleportTargetRef");
const subMenuObserver = new ResizeObserver(_calculateSubMenuLeft);
const menuStyle = ref<CSSProperties>({ display: "none" });
const subMenuStyle = ref<CSSProperties>({ display: "none" });
const width = ref(250);
const itemHeight = ref(34);
const subMenuItem = ref<MenuEntry>();
const separatorHeight = ref(1);
let teleportedEl: HTMLElement | null;

const props = withDefaults(defineProps<BcMenuProps>(), {
  x: 0,
  y: 0,
  items: () => [],
  isVisible: true,
  separateAll: false,
  numberOfVisibleItems: undefined,
  disableHeightCalculation: false,
  parentRect: null,
  placement: "bottom-start",
  fullWidth: false,
  textWrap: false,
  subItemIcon: undefined,
  selectedItem: undefined,
  offset: 0,
  teleportTarget: undefined,
});

onMounted(async () => {
  document.addEventListener("scroll", update);
  window.addEventListener("resize", update);
  if (props.isVisible) {
    await calculateMenuStyle();
  }

  if (props.teleportTarget && teleportTargetRef?.value) {
    const target = document.querySelector(props.teleportTarget);
    teleportedEl = teleportTargetRef.value;
    target?.appendChild(teleportedEl);
  }
});

onUnmounted(() => {
  document.removeEventListener("scroll", update);
  window.removeEventListener("resize", update);
  subMenuObserver.disconnect();
  teleportedEl?.remove();
});

function getMenuHeight(items: Array<MenuEntry>): number {
  return props.numberOfVisibleItems && props.numberOfVisibleItems > 0
    ? props.numberOfVisibleItems * itemHeight.value +
        props.numberOfVisibleItems * separatorHeight.value
    : items.length * itemHeight.value + getSeparatorsHeight(items);
}

function getSeparatorsHeight(items: Array<MenuEntry>, maxIndex: number = -1): number {
  let itemsWithSeparators = items;

  if (maxIndex > -1) {
    itemsWithSeparators = itemsWithSeparators.filter((_, idx) => idx < maxIndex);
  }

  if (!props.separateAll) {
    itemsWithSeparators = itemsWithSeparators.filter((x) => x.hasSeparator);
  }

  return itemsWithSeparators.length * separatorHeight.value;
}

function calcItemHeight(): void {
  const firstItem = menuRef?.value?.getElementsByClassName(
    "menuItem",
  )[0] as HTMLDivElement;
  const firstItemRect = firstItem?.getBoundingClientRect();
  if (!firstItem || firstItemRect?.height == 0) {
    itemHeight.value = 34;
    return;
  }

  const firstItemStyle = window.getComputedStyle(firstItem);
  itemHeight.value =
    firstItemRect.height +
    (parseFloat(firstItemStyle.marginTop) ?? 0) +
    (parseFloat(firstItemStyle.marginBottom) ?? 0);
}

function update(): void {
  if (!props.isVisible) return;
  requestAnimationFrame(calculateMenuStyle);
}

function calcFixedPosition(): void {
  const position = [props.x, props.y];
  const posX = Math.max(0, Math.min(window.innerWidth - width.value, position[0]));
  const posY = Math.max(
    0,
    Math.min(window.innerHeight - getMenuHeight(props.items), position[1]),
  );
  menuStyle.value.left = `${posX}px`;
  menuStyle.value.top = `${posY}px`;
}

async function calcPlacementPosition(): Promise<void> {
  const parentRect = props.parentRect?.getBoundingClientRect();

  if (!parentRect) {
    return;
  }

  if (props.fullWidth || props.textWrap) {
    menuStyle.value["min-width"] = parentRect.width + "px";
  }

  let height = getMenuHeight(props.items);

  await nextTick();
  width.value = menuRef.value?.offsetWidth ?? width.value;

  if (props.placement.startsWith("bottom") || props.placement.startsWith("top")) {
    if (props.placement.startsWith("bottom"))
      menuStyle.value.top = Math.max(0, parentRect.bottom + props.offset) + "px";
    else menuStyle.value.top = Math.max(0, parentRect.top - height - props.offset) + "px";

    if (props.placement.endsWith("start"))
      menuStyle.value.left = Math.max(0, parentRect.left) + "px";
    else if (props.placement.endsWith("end"))
      menuStyle.value.left = Math.max(0, parentRect.right - width.value) + "px";
    else
      menuStyle.value.left =
        Math.max(0, parentRect.right - parentRect.width / 2 - width.value / 2) + "px";
  } else if (props.placement.startsWith("left") || props.placement.startsWith("right")) {
    if (props.placement.startsWith("left"))
      menuStyle.value.left =
        Math.max(0, parentRect.left - width.value - props.offset) + "px";
    else menuStyle.value.left = Math.max(0, parentRect.right + props.offset) + "px";

    if (props.placement.endsWith("start"))
      menuStyle.value.top = Math.max(0, parentRect.top) + "px";
    else if (props.placement.endsWith("end"))
      menuStyle.value.top = Math.max(0, parentRect.bottom - height) + "px";
    else
      menuStyle.value.top =
        Math.max(0, parentRect.top + parentRect.height / 2 - height / 2) + "px";
  }

  if (
    menuStyle.value.left !== undefined &&
    !Number.isNaN(
      typeof menuStyle.value.left === "string"
        ? Number.parseFloat(menuStyle.value.left)
        : menuStyle.value.left,
    )
  ) {
    menuStyle.value.maxWidth = `${
      window.innerWidth -
      (typeof menuStyle.value.left === "string"
        ? Number.parseFloat(menuStyle.value.left)
        : menuStyle.value.left) -
      4
    }px`;
  }
  if (
    menuStyle.value.top &&
    !Number.isNaN(
      typeof menuStyle.value.top === "string"
        ? Number.parseFloat(menuStyle.value.top)
        : menuStyle.value.top,
    )
  ) {
    menuStyle.value.maxHeight = `${
      window.innerHeight -
      (typeof menuStyle.value.top === "string"
        ? Number.parseFloat(menuStyle.value.top)
        : menuStyle.value.top) -
      4
    }px`;
  }

  const menuHeight = getMenuHeight(props.items);
  if (menuHeight > 0 && !props.disableHeightCalculation) {
    const maxHeight = props.placement.startsWith("bottom")
      ? window.innerHeight - parentRect.bottom
      : parentRect.top;
    height = Math.min(Math.floor(maxHeight) - 1, menuHeight);
    menuStyle.value.maxHeight = height + "px";
    menuStyle.value.overflowY = "auto";
  }
}

async function calculateMenuStyle(): Promise<void> {
  subMenuStyle.value.opacity = 0;
  subMenuStyle.value.display = "none";

  if (!menuRef.value || menuRef.value.offsetWidth == 0) {
    menuStyle.value = {
      display: undefined,
      opacity: 0,
    };
    await nextTick();
    calcItemHeight();
    width.value = menuRef.value?.offsetWidth ?? width.value;
    const menuItemSeparators = menuRef.value?.getElementsByClassName("menuItemSeparator");
    if (menuItemSeparators && menuItemSeparators.length > 0)
      separatorHeight.value = menuItemSeparators[0].getBoundingClientRect().height;
  }

  const hasNumberOfVisibleItems =
    props.numberOfVisibleItems !== undefined &&
    props.numberOfVisibleItems > 0 &&
    props.items.some((i) => (i.subItems?.length ?? 0) > 0);

  menuStyle.value = {
    display: props.isVisible ? undefined : "none",
  };

  if (!props.isVisible) return;

  if (!props.disableHeightCalculation && hasNumberOfVisibleItems) {
    menuStyle.value.maxHeight = getMenuHeight(props.items) + "px";
    menuStyle.value.overflowY = "auto";
  }

  if (props.parentRect) {
    await calcPlacementPosition();
  } else {
    calcFixedPosition();
  }

  _scrollToSelectedItem();
}

function getAvailableItems(items: MenuEntry[]): MenuEntry[] {
  return items.filter((i) => i.component || (i.text && i.text !== ""));
}

async function calculateSubMenuStyle(item: MenuEntry, itemIdx: number): Promise<void> {
  if (
    !menuRef.value ||
    !subMenuRef.value ||
    !item.subItems ||
    item.subItems.length == 0 ||
    !props.isVisible
  ) {
    subMenuStyle.value = {
      opacity: 0,
      display: "none",
    };
    return;
  }

  subMenuStyle.value.display = "";
  subMenuItem.value = item;
  await nextTick();

  const menuRect = menuRef.value.getBoundingClientRect();
  const scrollPosition = menuRef.value.scrollTop;
  const swapSubItemsVertical = _shouldOpenUpwards(menuRect, itemIdx, item.subItems);
  const extraHeight = swapSubItemsVertical
    ? (itemIdx + 1) * itemHeight.value + getSeparatorsHeight(props.items, itemIdx + 1)
    : itemIdx * itemHeight.value + getSeparatorsHeight(props.items, itemIdx);

  subMenuStyle.value = {
    top: swapSubItemsVertical
      ? menuRect.top + extraHeight - scrollPosition - getMenuHeight(item.subItems) + "px"
      : menuRect.top + extraHeight - scrollPosition + "px",
  };
  _calculateSubMenuLeft();
}

function _calculateSubMenuLeft(): void {
  if (!menuRef.value || !subMenuRef.value || !props.isVisible) {
    return;
  }

  const subMenuWidth = subMenuRef.value.offsetWidth;
  const menuRect = menuRef.value.getBoundingClientRect();
  const swapSubItemsHorizontal = window.innerWidth <= menuRect.right + subMenuWidth;

  subMenuStyle.value.left = swapSubItemsHorizontal
    ? menuRect.left - subMenuWidth + 4 + "px"
    : menuRect.right - 4 + "px";
}

function _shouldOpenUpwards(
  menuRect: DOMRect,
  itemIndex: number,
  subActions: Array<MenuEntry>,
): boolean {
  const actionTop =
    menuRect.top +
    itemIndex * itemHeight.value +
    getSeparatorsHeight(props.items, itemIndex);

  //Check if we can open down
  const subActionsBottom =
    actionTop + subActions.length * itemHeight.value + getSeparatorsHeight(subActions);
  if (subActionsBottom <= window.innerHeight) {
    return false;
  }

  //IF not, check in which direction ther is more space
  const actionBottom = actionTop + itemHeight.value;
  return actionTop > window.innerHeight - actionBottom;
}

function _scrollToSelectedItem(): void {
  nextTick(() => {
    const selectedMenuItem = menuRef.value?.getElementsByClassName("selected");
    if (selectedMenuItem && selectedMenuItem.length > 0) {
      selectedMenuItem[0].scrollIntoView({ block: "nearest" });
    } else if ((menuRef.value?.getElementsByClassName("menuItem").length ?? 0) > 0) {
      menuRef.value
        ?.getElementsByClassName("menuItem")[0]
        .scrollIntoView({ block: "nearest" });
    }
  });
}

watch(props, calculateMenuStyle);
watch(subMenuRef, (val, oldVal) => {
  if (oldVal) subMenuObserver.unobserve(oldVal);
  if (val) subMenuObserver.observe(val);
});

defineExpose({
  update,
  menuStyle: menuStyle.value,
});
</script>

<template>
  <div ref="teleportTargetRef">
    <div
      class="bissantzControls menuControl"
      ref="menuRef"
      v-bind:data-pak="pak"
      v-bind:data-design="design"
      v-bind:style="menuStyle"
    >
      <slot>
        <BcMenuEntry
          v-for="(item, itemIdx) in getAvailableItems(items)"
          v-bind:key="item.key ?? itemIdx"
          v-bind:item="item"
          v-bind:subItemIcon="subItemIcon"
          v-bind:hasSeparator="
            (separateAll || item.hasSeparator) && itemIdx < items.length - 1
          "
          v-bind:selected="
            !!selectedItem &&
            selectedItem.key == item.key &&
            selectedItem.text == item.text
          "
          v-bind:textWrap="textWrap"
          v-bind:hasIcons="items.findIndex((i) => i.icon) != -1"
          v-bind:hasSubText="items.findIndex((i) => i.subText) != -1"
          v-on:click="emit('click', $event)"
          v-on:mouseenter="calculateSubMenuStyle(item, itemIdx)"
        >
          <template v-slot:before-item>
            <slot name="before-item" v-bind:entry="item" v-bind:entryIdx="itemIdx" />
          </template>
        </BcMenuEntry>
      </slot>
    </div>

    <div
      v-if="getAvailableItems(items).find((i) => i.subItems)"
      class="bissantzControls menuControl subMenuControl"
      ref="subMenuRef"
      v-bind:data-pak="pak"
      v-bind:data-design="design"
      v-bind:style="subMenuStyle"
    >
      <template v-if="subMenuItem?.subItems && subMenuItem.subItems.length > 0">
        <BcMenuEntry
          v-for="(subItem, subItemIdx) in getAvailableItems(subMenuItem.subItems)"
          v-bind:key="subItem.key ?? subItemIdx"
          v-bind:item="subItem"
          v-bind:hasSeparator="
            (separateAll || subItem.hasSeparator) &&
            subItemIdx < subMenuItem.subItems.length - 1
          "
          v-on:click="emit('click', $event)"
        />
      </template>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "../../styles/components/menu/bc-menu.less";
</style>
